import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

interface ProductTourContextValue {
  isTourActive: boolean;
  setTourActive: (active: boolean) => void;
  startTour: (scope?: 'full' | 'main-preferences') => void;
  registerStartTour: (fn: () => void) => void;
}

const noop = () => {};

const ProductTourContext = createContext<ProductTourContextValue>({
  isTourActive: false,
  setTourActive: noop,
  startTour: noop,
  registerStartTour: noop,
});

export function ProductTourProvider({ children }: { children: React.ReactNode }) {
  const [isTourActive, setTourActive] = useState(false);
  // `ProductTour` is the only component that can actually start the
  // tour but the launcher button lives in the sidebar.
  const startTourRef = useRef<() => void>(noop);

  const registerStartTour = useCallback((fn: () => void) => {
    startTourRef.current = fn;
  }, []);

  const startTour = useCallback(() => {
    startTourRef.current();
  }, []);

  const value = useMemo(
    () => ({ isTourActive, setTourActive, startTour, registerStartTour }),
    [isTourActive, startTour, registerStartTour],
  );

  return <ProductTourContext.Provider value={value}>{children}</ProductTourContext.Provider>;
}

export function useProductTour() {
  return useContext(ProductTourContext);
}
